/**
 * KD-3745 — the English nationality of each country, keyed by ISO 3166-1
 * alpha-2 code.
 *
 * Nationality fields store and send the alpha-2 code; only what is displayed
 * changes, from the country name to the nationality. Labels are English
 * whatever the interface language, which is why they live here and not in the
 * locale files.
 *
 * Every label is unique, so a list built from this table never shows two
 * identical options: a demonym shared by two countries carries the country
 * between brackets. Territories with no established demonym fall back on
 * their English name.
 */
export const NATIONALITIES: Readonly<Record<string, string>> = Object.freeze({
  AD: 'Andorran',
  AE: 'Emirati',
  AF: 'Afghan',
  AG: 'Antiguan and Barbudan',
  AI: 'Anguillan',
  AL: 'Albanian',
  AM: 'Armenian',
  AO: 'Angolan',
  AQ: 'Antarctica',
  AR: 'Argentinian',
  AS: 'American Samoan',
  AT: 'Austrian',
  AU: 'Australian',
  AW: 'Aruban',
  AX: 'Ålander',
  AZ: 'Azerbaijani',
  BA: 'Bosnian and Herzegovinian',
  BB: 'Barbadian',
  BD: 'Bangladeshi',
  BE: 'Belgian',
  BF: 'Burkinabe',
  BG: 'Bulgarian',
  BH: 'Bahraini',
  BI: 'Burundian',
  BJ: 'Beninese',
  BL: 'Barthélemois',
  BM: 'Bermudian',
  BN: 'Bruneian',
  BO: 'Bolivian',
  BQ: 'Bonaire, Sint Eustatius and Saba',
  BR: 'Brazilian',
  BS: 'Bahamian',
  BT: 'Bhutanese',
  BV: 'Bouvet Island',
  BW: 'Botswanan',
  BY: 'Belarusian',
  BZ: 'Belizean',
  CA: 'Canadian',
  CC: 'Cocos Islander',
  CD: 'Congolese (DR Congo)',
  CF: 'Central African',
  CG: 'Congolese (Congo)',
  CH: 'Swiss',
  CI: 'Ivorian',
  CK: 'Cook Islander',
  CL: 'Chilean',
  CM: 'Cameroonian',
  CN: 'Chinese',
  CO: 'Colombian',
  CR: 'Costa Rican',
  CU: 'Cuban',
  CV: 'Cape Verdean',
  CW: 'Curaçaoan',
  CX: 'Christmas Islander',
  CY: 'Cypriot',
  CZ: 'Czech',
  DE: 'German',
  DJ: 'Djiboutian',
  DK: 'Danish',
  DM: 'Dominican (Dominica)',
  DO: 'Dominican (Dominican Republic)',
  DZ: 'Algerian',
  EC: 'Ecuadorian',
  EE: 'Estonian',
  EG: 'Egyptian',
  EH: 'Sahrawi',
  ER: 'Eritrean',
  ES: 'Spanish',
  ET: 'Ethiopian',
  FI: 'Finnish',
  FJ: 'Fijian',
  FK: 'Falkland Islander',
  FM: 'Micronesian',
  FO: 'Faroese',
  FR: 'French',
  GA: 'Gabonese',
  GB: 'British',
  GD: 'Grenadian',
  GE: 'Georgian',
  GF: 'French Guianese',
  GG: 'Guernsey',
  GH: 'Ghanaian',
  GI: 'Gibraltarian',
  GL: 'Greenlandic',
  GM: 'Gambian',
  GN: 'Guinean',
  GP: 'Guadeloupean',
  GQ: 'Equatorial Guinean',
  GR: 'Greek',
  GS: 'South Georgia and the South Sandwich Islands',
  GT: 'Guatemalan',
  GU: 'Guamanian',
  GW: 'Bissau-Guinean',
  GY: 'Guyanese',
  HK: 'Hongkonger',
  HM: 'Heard Island and McDonald Islands',
  HN: 'Honduran',
  HR: 'Croatian',
  HT: 'Haitian',
  HU: 'Hungarian',
  ID: 'Indonesian',
  IE: 'Irish',
  IL: 'Israeli',
  IM: 'Manx',
  IN: 'Indian',
  IO: 'British Indian Ocean Territory',
  IQ: 'Iraqi',
  IR: 'Iranian',
  IS: 'Icelandic',
  IT: 'Italian',
  JE: 'Jersey',
  JM: 'Jamaican',
  JO: 'Jordanian',
  JP: 'Japanese',
  KE: 'Kenyan',
  KG: 'Kyrgyz',
  KH: 'Cambodian',
  KI: 'I-Kiribati',
  KM: 'Comorian',
  KN: 'Kittitian and Nevisian',
  KP: 'North Korean',
  KR: 'South Korean',
  KW: 'Kuwaiti',
  KY: 'Caymanian',
  KZ: 'Kazakh',
  LA: 'Lao',
  LB: 'Lebanese',
  LC: 'Saint Lucian',
  LI: 'Liechtensteiner',
  LK: 'Sri Lankan',
  LR: 'Liberian',
  LS: 'Basotho',
  LT: 'Lithuanian',
  LU: 'Luxembourgish',
  LV: 'Latvian',
  LY: 'Libyan',
  MA: 'Moroccan',
  MC: 'Monegasque',
  MD: 'Moldovan',
  ME: 'Montenegrin',
  MF: 'Saint Martiner',
  MG: 'Malagasy',
  MH: 'Marshallese',
  MK: 'Macedonian',
  ML: 'Malian',
  MM: 'Burmese',
  MN: 'Mongolian',
  MO: 'Macanese',
  MP: 'Northern Mariana Islander',
  MQ: 'Martinican',
  MR: 'Mauritanian',
  MS: 'Montserratian',
  MT: 'Maltese',
  MU: 'Mauritian',
  MV: 'Maldivian',
  MW: 'Malawian',
  MX: 'Mexican',
  MY: 'Malaysian',
  MZ: 'Mozambican',
  NA: 'Namibian',
  NC: 'New Caledonian',
  NE: 'Nigerien',
  NF: 'Norfolk Islander',
  NG: 'Nigerian',
  NI: 'Nicaraguan',
  NL: 'Dutch',
  NO: 'Norwegian',
  NP: 'Nepalese',
  NR: 'Nauruan',
  NU: 'Niuean',
  NZ: 'New Zealander',
  OM: 'Omani',
  PA: 'Panamanian',
  PE: 'Peruvian',
  PF: 'French Polynesian',
  PG: 'Papua New Guinean',
  PH: 'Filipino',
  PK: 'Pakistani',
  PL: 'Polish',
  PM: 'Saint-Pierrais and Miquelonnais',
  PN: 'Pitcairn Islander',
  PR: 'Puerto Rican',
  PS: 'Palestinian',
  PT: 'Portuguese',
  PW: 'Palauan',
  PY: 'Paraguayan',
  QA: 'Qatari',
  RE: 'Réunionese',
  RO: 'Romanian',
  RS: 'Serbian',
  RU: 'Russian',
  RW: 'Rwandan',
  SA: 'Saudi',
  SB: 'Solomon Islander',
  SC: 'Seychellois',
  SD: 'Sudanese',
  SE: 'Swedish',
  SG: 'Singaporean',
  SH: 'Saint Helenian',
  SI: 'Slovenian',
  SJ: 'Svalbard and Jan Mayen',
  SK: 'Slovak',
  SL: 'Sierra Leonean',
  SM: 'Sammarinese',
  SN: 'Senegalese',
  SO: 'Somali',
  SR: 'Surinamese',
  SS: 'South Sudanese',
  ST: 'Santomean',
  SV: 'Salvadoran',
  SX: 'Sint Maartener',
  SY: 'Syrian',
  SZ: 'Swazi',
  TC: 'Turks and Caicos Islander',
  TD: 'Chadian',
  TF: 'French Southern Territories',
  TG: 'Togolese',
  TH: 'Thai',
  TJ: 'Tajik',
  TK: 'Tokelauan',
  TL: 'East Timorese',
  TM: 'Turkmen',
  TN: 'Tunisian',
  TO: 'Tongan',
  TR: 'Turkish',
  TT: 'Trinidadian and Tobagonian',
  TV: 'Tuvaluan',
  TW: 'Taiwanese',
  TZ: 'Tanzanian',
  UA: 'Ukrainian',
  UG: 'Ugandan',
  UM: 'United States Minor Outlying Islands',
  US: 'American',
  UY: 'Uruguayan',
  UZ: 'Uzbek',
  VA: 'Vatican',
  VC: 'Vincentian',
  VE: 'Venezuelan',
  VG: 'British Virgin Islander',
  VI: 'US Virgin Islander',
  VN: 'Vietnamese',
  VU: 'Ni-Vanuatu',
  WF: 'Wallisian and Futunan',
  WS: 'Samoan',
  XK: 'Kosovar',
  YE: 'Yemeni',
  YT: 'Mahoran',
  ZA: 'South African',
  ZM: 'Zambian',
  ZW: 'Zimbabwean',
});

export type NationalityOption = {
  value: string;
  label: string;
};

// KD-3973 — Bouvet Island is uninhabited: no traveller holds its nationality.
// It stays in the table above so that a record saved with it still displays.
const NOT_OFFERED = new Set(['BV']);

/**
 * KD-3973 — the options of every nationality field, sorted by label. They come
 * from the table above and not from the catalog countries, which only hold
 * the destinations Koob sells and missed most nationalities.
 */
export const NATIONALITY_OPTIONS: readonly NationalityOption[] = Object.freeze(
  Object.entries(NATIONALITIES)
    .filter(([code]) => !NOT_OFFERED.has(code))
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'en')),
);

/**
 * Nationality fields do not agree on what they hold: a plain code, a select
 * option ({ value }) or an API country ({ alpha2 }). An empty code yields
 * undefined, whatever its shape. `toFieldValue` of @koob/booking delegates here.
 */
export function toNationalityCode(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const option = value as { value?: string; alpha2?: string };
    return option.value || option.alpha2 || undefined;
  }
  return undefined;
}

/**
 * The label to display for a nationality. A code missing from the table (not
 * ISO, created by hand, legacy free text) is shown as the fallback, else as is,
 * so that nothing a traveller was saved with ever disappears from the screen.
 */
export function getNationalityLabel(
  value: unknown,
  fallback?: string,
): string | undefined {
  const code = toNationalityCode(value);
  if (!code) return undefined;
  return NATIONALITIES[code.toUpperCase()] ?? fallback ?? code;
}
